import i18n from "../../i18n";

const SETTINGS_UPDATED_EVENT = "paranoidi:settings-updated";

const defaultSettings = {
  isLiveActive: false,
  maxRequestsPerSet: 20,
  publicSongLanguageMode: "all",
  currentSetName: "1. setti",
  publicTitle: "",
  publicIntro: "",
  publicTitleEn: "",
  publicIntroEn: "",
  seoTitle: "",
  seoDescription: "",
  seoTitleEn: "",
  seoDescriptionEn: "",
  faqItems: [],
  faqItemsEn: [],
  heroImageUrl: "",
  whatsappNumber: "",
  whatsappMessage: "",
  whatsappFloatingEnabled: false,
  websiteUrl: "",
  instagramUrl: "",
  tiktokUrl: "",
  youtubeUrl: "",
  facebookUrl: "",
  spotifyUrl: "",

  // Maksullinen biisitoiveen näkyvyysnosto.
  paidRequestBoostsEnabled: false,
  paidRequestBoostAmountCents: 300,

  // Nykyisen keikkasession aloitusaika.
  // null = "Aloita uusi keikka" -toimintoa ei ole vielä käytetty.
  currentGigStartedAt: null,
};

function getApiErrorMessage(fi, en) {
  const language = String(
    i18n.resolvedLanguage || i18n.language || "fi"
  ).toLowerCase();

  return language.startsWith("en") ? en : fi;
}

function createApiError(fi, en, data = null) {
  const error = new Error(getApiErrorMessage(fi, en));

  error.code = data?.code || "";
  error.payload = data || {};
  error.backendMessage = String(data?.error || "").trim();

  return error;
}

function getApiBaseUrl() {
  return (
    window.PARANOIDI_API_BASE_URL ||
    (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
      ? "http://localhost:4000/api"
      : "/backend/api")
  );
}

function getPerformerSlug() {
  try {
    const performer = JSON.parse(
      window.sessionStorage.getItem("paranoidi_auth_performer") || "{}"
    );

    const slug = String(performer?.slug || "").trim();

    if (slug) {
      return slug;
    }
  } catch {
    // Käytetään fallbackia alla.
  }

  return "demo";
}

function normalizeSettings(settings = {}) {
  return {
    ...defaultSettings,
    ...settings,

    isLiveActive: Boolean(
      settings?.isLiveActive ?? settings?.is_live_active
    ),

    maxRequestsPerSet:
      Number(
        settings?.maxRequestsPerSet ??
          settings?.max_requests_per_set
      ) || defaultSettings.maxRequestsPerSet,

    publicSongLanguageMode:
      settings?.publicSongLanguageMode ||
      settings?.public_song_language_mode ||
      defaultSettings.publicSongLanguageMode,

    publicTitle:
      settings?.publicTitle ||
      settings?.public_title ||
      "",

    publicIntro:
      settings?.publicIntro ||
      settings?.public_intro ||
      "",

    publicTitleEn:
      settings?.publicTitleEn ||
      settings?.public_title_en ||
      "",

    publicIntroEn:
      settings?.publicIntroEn ||
      settings?.public_intro_en ||
      "",

    seoTitle:
      settings?.seoTitle ||
      settings?.seo_title ||
      "",

    seoDescription:
      settings?.seoDescription ||
      settings?.seo_description ||
      "",

    seoTitleEn:
      settings?.seoTitleEn ||
      settings?.seo_title_en ||
      "",

    seoDescriptionEn:
      settings?.seoDescriptionEn ||
      settings?.seo_description_en ||
      "",

    faqItems:
      Array.isArray(settings?.faqItems)
        ? settings.faqItems
        : Array.isArray(settings?.faq_items)
          ? settings.faq_items
          : [],

    faqItemsEn:
      Array.isArray(settings?.faqItemsEn)
        ? settings.faqItemsEn
        : Array.isArray(settings?.faq_items_en)
          ? settings.faq_items_en
          : [],

    heroImageUrl:
      settings?.heroImageUrl ||
      settings?.hero_image_url ||
      "",

    whatsappNumber:
      settings?.whatsappNumber ||
      settings?.whatsapp_number ||
      "",

    whatsappMessage:
      settings?.whatsappMessage ||
      settings?.whatsapp_message ||
      "",

    whatsappFloatingEnabled: Boolean(
      settings?.whatsappFloatingEnabled ??
        settings?.whatsapp_floating_enabled ??
        false
    ),

    websiteUrl:
      settings?.websiteUrl ||
      settings?.website_url ||
      "",

    instagramUrl:
      settings?.instagramUrl ||
      settings?.instagram_url ||
      "",

    tiktokUrl:
      settings?.tiktokUrl ||
      settings?.tiktok_url ||
      "",

    youtubeUrl:
      settings?.youtubeUrl ||
      settings?.youtube_url ||
      "",

    facebookUrl:
      settings?.facebookUrl ||
      settings?.facebook_url ||
      "",

    spotifyUrl:
      settings?.spotifyUrl ||
      settings?.spotify_url ||
      "",

    paidRequestBoostsEnabled: Boolean(
      settings?.paidRequestBoostsEnabled ??
        settings?.paid_request_boosts_enabled ??
        false
    ),

    paidRequestBoostAmountCents:
      Number(
        settings?.paidRequestBoostAmountCents ??
          settings?.paid_request_boost_amount_cents
      ) || 300,

    // Backend palauttaa ajan joko camelCase- tai snake_case-muodossa.
    currentGigStartedAt:
      settings?.currentGigStartedAt ||
      settings?.current_gig_started_at ||
      null,
  };
}

export function getParanoidiSettings() {
  return defaultSettings;
}

export async function fetchParanoidiSettings() {
  try {
    const performerSlug = getPerformerSlug();

    const response = await fetch(
      `${getApiBaseUrl()}/public/performers/${encodeURIComponent(
        performerSlug
      )}`,
      {
        credentials: "include",
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw createApiError(
        "Asetusten haku epäonnistui.",
        "Could not load settings.",
        data
      );
    }

    return normalizeSettings(
      data.performer?.settings || {}
    );
  } catch (error) {
    console.error(
      "Paranoidi-asetusten haku epäonnistui:",
      error
    );

    return defaultSettings;
  }
}

export async function saveParanoidiSettings(settings) {
  try {
    const performerSlug = getPerformerSlug();

    const response = await fetch(
      `${getApiBaseUrl()}/admin/performers/${encodeURIComponent(
        performerSlug
      )}/settings`,
      {
        method: "POST",
        headers: {
          "Content-Type":
            "application/json; charset=utf-8",
        },
        credentials: "include",
        body: JSON.stringify(settings),
      }
    );

    const data = await response.json();

    if (!response.ok || !data.ok) {
      throw createApiError(
        "Asetusten tallennus epäonnistui.",
        "Could not save settings.",
        data
      );
    }

    const nextSettings = normalizeSettings(
      data.settings || {}
    );

    window.dispatchEvent(
      new CustomEvent(SETTINGS_UPDATED_EVENT, {
        detail: nextSettings,
      })
    );

    return nextSettings;
  } catch (error) {
    console.error(
      "Paranoidi-asetusten tallennus epäonnistui:",
      error
    );

    throw error;
  }
}

export function subscribeParanoidiSettings(callback) {
  function handleUpdate(event) {
    if (event.detail) {
      callback(normalizeSettings(event.detail));
    }
  }

  window.addEventListener(
    SETTINGS_UPDATED_EVENT,
    handleUpdate
  );

  return () => {
    window.removeEventListener(
      SETTINGS_UPDATED_EVENT,
      handleUpdate
    );
  };
}
